<template>
  <div ref="dropdown" class="relative w-full">
    <component
      ref="triggerRef"
      :is="$slots.trigger ? 'div' : 'button'"
      @click="toggleMenu"
      :class="[
        'flex items-center cursor-pointer transition-all duration-300',
        $slots.trigger && 'w-full',
        !$slots.trigger && 'rounded-full p-1 hover:bg-gray-100'
      ]"
    >
      <slot name="trigger">
        <img
          v-if="userStore.currentUser?.avatar_image"
          :src="userStore.currentUser.avatar_image"
          alt="Profile image"
          class="h-8 w-8 rounded-full object-cover"
        />
        <span
          v-else
          class="h-8 w-8 rounded-full flex items-center justify-center bg-gray-100"
        >
          <User class="w-5 h-5 text-gray-500" />
        </span>
      </slot>
    </component>

    <Teleport to="body" :disabled="!avoidOverflow">
      <transition
        enter-active-class="transition ease-out duration-200"
        :enter-from-class="openUpwards ? 'transform opacity-0 translate-y-2' : 'transform opacity-0 scale-95'"
        :enter-to-class="openUpwards ? 'transform opacity-100 translate-y-0' : 'transform opacity-100 scale-100'"
        leave-active-class="transition ease-in duration-150"
        :leave-from-class="openUpwards ? 'transform opacity-100 translate-y-0' : 'transform opacity-100 scale-100'"
        :leave-to-class="openUpwards ? 'transform opacity-0 translate-y-2' : 'transform opacity-0 scale-95'"
      >
        <div
          ref="menuRef"
          v-show="showProfileMenu"
          class="cursor-default w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-[100]"
          :class="{
            'absolute bottom-full mb-3': !avoidOverflow && openUpwards,
            'absolute right-0 mt-2 origin-top-right': !avoidOverflow && !openUpwards,
            'fixed': avoidOverflow,
            'left-0': !avoidOverflow && openUpwards && alignLeft,
            '-right-4': !avoidOverflow && openUpwards && !alignLeft
          }"
          :style="avoidOverflow ? fixedPositionStyle : {}"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="menu-button"
          tabindex="-1"
        >
          <div class="py-1" role="none">
            <div class="px-4 py-2 border-b border-gray-100">
              <span class="block text-sm text-gray-900 truncate">
                {{ userStore.currentUser?.first_name }} {{ userStore.currentUser?.last_name }}
              </span>
              <span class="block text-gray-500 truncate text-xs">
                {{ userStore.currentUser?.email }}
              </span>
            </div>

            <a
              :href="profileUrl"
              :class="menuItemClass"
              role="menuitem"
              @click="close"
            >
              <UserRound class="inline h-4 mr-2" />
              My profile
            </a>

            <router-link
              :to="APP_ROUTES.ACCOUNT"
              :class="menuItemClass"
              role="menuitem"
              @click="close"
            >
              <Settings class="inline h-4 mr-2" />
              Account Settings
            </router-link>

            <router-link
              :to="APP_ROUTES.TUITION_MANAGE"
              :class="menuItemClass"
              role="menuitem"
              @click="close"
            >
              <CreditCard class="inline h-4 mr-2" />
              My Tuition
            </router-link>

            <a
              :class="menuItemClass"
              role="menuitem"
              @click="logOut"
            >
              <LogOut class="inline-block h-4 mr-2" />
              Logout
            </a>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { User, LogOut, UserRound, Settings, CreditCard } from 'lucide-vue-next'
import { useUserStore } from '@/stores/user'
import { APP_ROUTES } from '@/const/appRoutes'

const OUTTALENT_DOMAIN = 'https://outtalent.com'

const menuItemClass = 'text-gray-700 px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center'

const props = defineProps<{
  openUpwards?: boolean
  alignLeft?: boolean
  avoidOverflow?: boolean
}>()

const userStore = useUserStore()
const router = useRouter()

const showProfileMenu = ref(false)
const dropdown = ref<HTMLElement | null>(null)
const triggerRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)
const fixedPositionStyle = ref<Record<string, string>>({})

const profileUrl = computed(() =>
  `${OUTTALENT_DOMAIN}/profile/${userStore.currentUser?.username}`
)

function updateFixedPosition() {
  if (!triggerRef.value || !props.avoidOverflow) return
  const rect = triggerRef.value.getBoundingClientRect()
  const gap = 12
  fixedPositionStyle.value = props.openUpwards
    ? { left: `${rect.left}px`, bottom: `${window.innerHeight - rect.top + gap}px` }
    : { left: `${rect.left}px`, top: `${rect.bottom + gap}px` }
}

function toggleMenu() {
  if (!showProfileMenu.value) updateFixedPosition()
  showProfileMenu.value = !showProfileMenu.value
}

function close() {
  showProfileMenu.value = false
}

async function logOut() {
  close()
  await userStore.logout()
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push('/login')
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target as Node
  if (!dropdown.value?.contains(target) && !menuRef.value?.contains(target)) {
    close()
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => document.removeEventListener('click', handleClickOutside))
</script>
